import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Tooltip,
  Button,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControl,
  Grid2
} from "@mui/material";
import { Autocomplete } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";

// FDI numbering for adult teeth (permanent teeth)
const FDI_TEETH = [
  // Upper right (18-11)
  18, 17, 16, 15, 14, 13, 12, 11,
  // Upper left (21-28)
  21, 22, 23, 24, 25, 26, 27, 28,
  // Lower left (38-31)
  38, 37, 36, 35, 34, 33, 32, 31,
  // Lower right (41-48)
  41, 42, 43, 44, 45, 46, 47, 48,
];

const UPPER_TOOTH_PATHS = {
  incisor: "M18 2 Q12 0 6 2 Q2 8 6 18 Q12 22 18 18 Q22 8 18 2 Z",
  canine: "M18 2 Q12 0 6 2 Q2 10 10 22 Q12 24 14 22 Q22 10 18 2 Z",
  premolar: "M18 2 Q12 0 6 2 Q2 10 8 18 Q12 22 18 18 Q24 10 18 2 Z",
  molar: "M18 2 Q12 0 6 2 Q2 10 6 18 Q8 22 12 18 Q16 22 18 18 Q24 10 18 2 Z",
};

const LOWER_TOOTH_PATHS = {
  incisor: "M18 23 Q12 25 6 23 Q2 17 6 7 Q12 3 18 7 Q22 17 18 23 Z",
  canine: "M18 23 Q12 25 6 23 Q2 15 10 3 Q12 1 14 3 Q22 15 18 23 Z",
  premolar: "M18 23 Q12 25 6 23 Q2 15 8 7 Q12 3 18 7 Q24 15 18 23 Z",
  molar: "M18 23 Q12 25 6 23 Q2 15 6 7 Q8 5 12 7 Q16 5 18 7 Q24 15 18 23 Z",
};

const getToothType = (tooth) => {
  const incisor = [11, 12, 21, 22, 31, 32, 41, 42];
  const canine = [13, 23, 33, 43];
  const premolar = [14, 15, 24, 25, 34, 35, 44, 45];
  if (incisor.includes(tooth)) return "incisor";
  if (canine.includes(tooth)) return "canine";
  if (premolar.includes(tooth)) return "premolar";
  return "molar";
};

const ClinicalExaminationToothChart = ({
  clinicalExamination, setClinicalExamination
}) => {
  const toothWidth = 36;
  const toothHeight = 48;
  const gap = 0;
  const [selectedTooth, setSelectedTooth] = useState(null);
  const [currentToothNote, setCurrentToothNote] = useState("");
  const [currentToothCondition, setCurrentToothCondition] = useState("");

  const handleToothClick = (tooth) => {
    setSelectedTooth(tooth);
    setCurrentToothNote(clinicalExamination.find((item) => item.toothNumber === tooth)?.toothNotes || "");
    setCurrentToothCondition(clinicalExamination.find((item) => item.toothNumber === tooth)?.toothCondition || "");
  };

  const handleSaveToothDetails = () => {
    if (!selectedTooth) return;
    if (
      (currentToothNote && currentToothNote.trim() !== "") ||
      (currentToothCondition && currentToothCondition.length > 0)
    ) {
      if (
        !clinicalExamination.some((item) => item.toothNumber === selectedTooth)
      ) {
        setClinicalExamination((prev) => [
          ...prev,
          {
            toothNumber: selectedTooth,
            toothNotes: currentToothNote,
            toothCondition: currentToothCondition || [],
          },
        ]);
      } else {
        // Update existing tooth details
        setClinicalExamination((prev) => {
          const updated = prev.map((item) =>
            item.toothNumber === selectedTooth
              ? {
                  ...item,
                  toothNotes: currentToothNote,
                  toothCondition: currentToothCondition || [],
                }
              : item
          );
          return updated;
        });
      } 

      setSelectedTooth(null);
      setCurrentToothNote("");
      setCurrentToothCondition("");
    }
  };

  const handleDeleteToothDetails = (tooth) => {
    setClinicalExamination((prev) => {
      const updated = prev.filter((item) => item.toothNumber !== tooth);
      return updated;
    });
  };

  const renderTeeth = () => {
    const upper = FDI_TEETH.slice(0, 16);
    const lower = FDI_TEETH.slice(16);

    return (
      <>
        {/* Upper teeth */}
        {upper.map((tooth, i) => (
          <g key={tooth}>
            <Tooltip
              title={`Tooth ${tooth}: ${
                clinicalExamination?.find((item) => item.toothNumber === tooth)?.toothCondition || "No condition"
              } - ${clinicalExamination?.find((item) => item.toothNumber === tooth)?.toothNotes || "No notes"}`}
            >
              <path
                d={UPPER_TOOTH_PATHS[getToothType(tooth)]}
                transform={`translate(${i * (toothWidth + gap)}, 0) scale(1.5)`}
                fill={
                  clinicalExamination?.find((item) => item.toothNumber === tooth)?.toothNotes ||
                  clinicalExamination?.find((item) => item.toothNumber === tooth)?.toothCondition
                    ? "#ffe082"
                    : "#e3f2fd"
                }
                stroke="#1976d2"
                strokeWidth={selectedTooth === tooth ? 4 : 2}
                style={{ cursor: "pointer" }}
                onClick={() => handleToothClick(tooth)}
              />
            </Tooltip>
            <text
              x={i * (toothWidth + gap) + toothWidth / 2}
              y={toothHeight / 2}
              textAnchor="middle"
              fontSize="14"
              fill="#1976d2"
              style={{ pointerEvents: "none", userSelect: "none" }}
            >
              {tooth}
            </text>
          </g>
        ))}
        {/* Lower teeth */}
        {lower.map((tooth, i) => (
          <g key={tooth}>
            <Tooltip
              title={`Tooth ${tooth}: ${
                clinicalExamination?.find((item) => item.toothNumber === tooth)?.toothCondition || "No condition"
              } - ${clinicalExamination?.find((item) => item.toothNumber === tooth)?.toothNotes || "No notes"}`}
            >
              <path
                d={LOWER_TOOTH_PATHS[getToothType(tooth)]}
                transform={`translate(${i * (toothWidth + gap)}, ${
                  toothHeight + gap
                }) scale(1.5)`}
                fill={
                  clinicalExamination?.find((item) => item.toothNumber === tooth)?.toothNotes ||
                  clinicalExamination?.find((item) => item.toothNumber === tooth)?.toothCondition
                    ? "#ffe082"
                    : "#e3f2fd"
                }
                stroke="#1976d2"
                strokeWidth={selectedTooth === tooth ? 4 : 2}
                style={{ cursor: "pointer" }}
                onClick={() => handleToothClick(tooth)}
              />
            </Tooltip>
            <text
              x={i * (toothWidth + gap) + toothWidth / 2}
              y={toothHeight + gap + toothHeight / 2}
              textAnchor="middle"
              fontSize="14"
              fill="#1976d2"
              style={{ pointerEvents: "none", userSelect: "none" }}
            >
              {tooth}
            </text>
          </g>
        ))}
      </>
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          width: (toothWidth + gap) * 16 + 25,
          height: (toothHeight + gap) * 2 + 25,
          overflowX: "auto",
          border: "1px solid #ddd",
          borderRadius: 2,
          background: "#fafafa",
          py: 1,
          mx: "auto",
        }}
      >
        <svg
          width={(toothWidth + gap) * 16}
          height={(toothHeight + gap) * 2}
          style={{ display: "block" }}
        >
          {renderTeeth()}
        </svg>
      </Box>

      {/* Teeth with Details List */}
      {clinicalExamination?.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Teeth wise examination details
          </Typography>
          <Grid container spacing={2}>
            {clinicalExamination?.map((toothDetails) => (
              <Grid key={toothDetails.toothNumber} item xs={6} sm={4} md={3} lg={2}>
                <Card variant="outlined" sx={{ position: "relative" }}>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteToothDetails(toothDetails.toothNumber)}
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      zIndex: 1,
                      backgroundColor: "rgba(255, 255, 255, 0.8)",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                      },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                  <CardContent>
                    <Typography color="primary" variant="subtitle1">
                      Tooth {toothDetails.toothNumber}
                    </Typography>
                    {toothDetails.toothCondition && (
                      <Typography variant="body2">
                        <strong>Condition:</strong> {toothDetails.toothCondition}
                      </Typography>
                    )}
                    {toothDetails.toothNotes && (
                      <Typography variant="body2">
                        <strong>Notes:</strong> {toothDetails.toothNotes}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Tooth Details Dialog */}
     <Dialog
  open={!!selectedTooth}
  onClose={() => setSelectedTooth(null)}
  maxWidth="md"
  fullWidth
  PaperProps={{
    sx: {
      height: '70vh', // Fixed height
      maxHeight: '70vh',
    }
  }}
>
  <DialogTitle>
    Tooth {selectedTooth} Details
  </DialogTitle>
  <DialogContent dividers sx={{ 
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: '#f1f1f1',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#888',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#555',
    },
  }}>
    <Grid2 container spacing={3}>
      {/* Condition Section */}
      <Grid2 size={12}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Condition
        </Typography>
        <Box
          sx={{
            p: 2,
            border: "1px solid #e0e0e0",
            borderRadius: 1,
            backgroundColor: "#f9f9f9",
          }}
        >
          <FormControl fullWidth>
            <Autocomplete
              multiple
              options={[
                "Caries",
                "Mobility",
                "Tenderness",
                "Swelling",
                "Sensitivity",
                "Fracture",
                "Missing",
                "Impacted",
              ]}
              disableCloseOnSelect
              value={
                Array.isArray(currentToothCondition)
                  ? currentToothCondition
                  : []
              }
              onChange={(event, newValue) => {
                setCurrentToothCondition(newValue);
              }}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    style={{ marginRight: 8 }}
                    checked={selected}
                    color="primary"
                  />
                  {option}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select conditions for this tooth"
                  variant="outlined"
                  size="small"
                />
              )}
            />
          </FormControl>
        </Box>
      </Grid2>

      {/* Notes Section */}
      <Grid2 size={12}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Notes
        </Typography>
        <Box
          sx={{
            p: 2,
            border: "1px solid #e0e0e0",
            borderRadius: 1,
            backgroundColor: "#f9f9f9",
          }}
        >
          <TextField
            label="Additional Notes"
            multiline
            rows={4}
            value={currentToothNote}
            onChange={(e) => setCurrentToothNote(e.target.value)}
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Enter detailed notes about this tooth..."
          />
        </Box>
      </Grid2>
    </Grid2>
  </DialogContent>
  <DialogActions>
    <Button 
      onClick={() => setSelectedTooth(null)} 
      variant="outlined" 
      color="inherit"
    >
      Cancel
    </Button>
    <Button
      variant="contained"
      color="primary"
      onClick={handleSaveToothDetails}
      disabled={!currentToothNote.trim() && !currentToothCondition?.length}
    >
      Save
    </Button>
  </DialogActions>
</Dialog>
    </Box>
  );
};

export default ClinicalExaminationToothChart;